import DOMPurify from 'dompurify';

/**
 * Hook: Remove dangerous attributes before they are applied.
 */
DOMPurify.addHook('uponSanitizeAttribute', (node: any, data) => {
    // Remove all inline JS event handlers
    if (data.attrName && data.attrName.startsWith('on')) {
        data.keepAttr = false;
    }

    // Prevent javascript: URLs
    if (data.attrName === 'href' || data.attrName === 'src') {
        const value = data.attrValue?.trim().toLowerCase();
        if (value?.startsWith('javascript:')) {
            data.keepAttr = false;
        }
    }
});

/**
 * Hook: Harden target after sanitization.
 */
DOMPurify.addHook('afterSanitizeAttributes', function (node: any) {
    if ('target' in node) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer'); // Recommended for security
    }
});

/**
 * Sanitizes an HTML string for safe rendering.
 *
 * @param html - Raw HTML string (possibly untrusted)
 * @returns A sanitized, safe-to-render HTML string
 *
 * Security Controls:
 * - Removes dangerous tags (script, embed, form, etc.)
 * - Removes inline event handlers
 * - Blocks javascript: URLs via hook (default URI regex also blocks them)
 * - Preserves safe HTML formatting
 *
 * NOTE:
 * Should be used together with an iframe sandbox for defense-in-depth.
 */
export function sanitizeHtml(html: string): string {
    return DOMPurify.sanitize(html, {
        // Allows standard safe HTML elements
        USE_PROFILES: { html: true },

        // Explicitly block dangerous elements
        FORBID_TAGS: ['script', 'object', 'embed', 'applet', 'form'],

        // Return the full HTML document so <style> tags stay in <head>
        // and the email template's document structure is preserved.
        WHOLE_DOCUMENT: true,

        // Allow <link> (external fonts/CSS) and <meta> (charset, viewport)
        // which are not in the default HTML profile but needed for email templates.
        ADD_TAGS: ['link', 'meta'],

        // Allow target for anchors, plus meta/link attributes not in the default profile.
        ADD_ATTR: ['target', 'content', 'http-equiv', 'charset'],
    });
}
